import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Shield,
  Target,
  Mail,
  BarChart3,
  Users,
  FileSpreadsheet,
  Zap,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Calendar,
  Download,
  Upload,
  Link as LinkIcon,
  Rocket,
  Gift,
  MessageSquare,
  TrendingUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const PROMO_CODE = "ANDCO2026";
const CTA_URL = "https://yourfreelanceos.com/signup?promo=ANDCO2026";
const DEADLINE = new Date("2026-03-01T00:00:00");

export default function AndCoMigrationPage() {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = DEADLINE - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(timer);
  }, []);

  const comparisonData = [
    {
      andco: "Project tracking",
      clientkit: "Projects + AI Scope Guard that catches scope creep in real-time",
      icon: Target
    },
    {
      andco: "Client database",
      clientkit: "Client Firewall that flags ghosters and scope creepers",
      icon: Shield
    },
    {
      andco: "Proposals",
      clientkit: "Proposals + built-in cold outreach tools",
      icon: Mail
    },
    {
      andco: "Notes & activity",
      clientkit: "Deal timelines, activity logging, and quick email logging",
      icon: MessageSquare
    },
  ];

  const features = [
    { icon: Shield, title: "Client Firewall", desc: "Flags risk signals from your client messages" },
    { icon: Target, title: "AI Scope Guard", desc: "Detects scope creep before it becomes a problem" },
    { icon: Mail, title: "Gmail Connect", desc: "Log and track messages from your inbox" },
    { icon: BarChart3, title: "Profitability Insights", desc: "See which clients and projects are worth your time" },
    { icon: Zap, title: "Cold Outreach", desc: "Land your next client before this one ends" },
    { icon: Users, title: "CRM Pipeline", desc: "Manage deals from Lead → Won" },
    { icon: Upload, title: "CSV Import", desc: "Quickly migrate your clients and deals from AND.CO" },
  ];

  const migrationSteps = [
    {
      step: 1,
      title: "Export from AND.CO",
      tasks: [
        "Go to Settings → Export Data",
        "Download CSVs for Clients, Projects, and Deals",
        "Save files to your computer"
      ],
      icon: Download,
      time: "5 min"
    },
    {
      step: 2,
      title: "Set up your ClientKit account",
      tasks: [
        "Sign up at yourfreelanceos.com",
        "Use the CSV Import tool in the CRM workspace",
        "Upload your data — fields map automatically"
      ],
      icon: Upload,
      time: "10 min"
    },
    {
      step: 3,
      title: "Recreate open projects",
      tasks: [
        "Create or import current projects",
        "Upload any scope documents or notes",
        "Turn on AI Scope Guard to monitor for creep"
      ],
      icon: FileSpreadsheet,
      time: "10 min"
    },
    {
      step: 4,
      title: "Connect your email",
      tasks: [
        "Link your Gmail account",
        "Log and track deal-related messages automatically",
        "Never miss a client communication"
      ],
      icon: LinkIcon,
      time: "5 min"
    },
  ];

  const faqs = [
    {
      q: "Can I import my AND.CO data?",
      a: "Yes. Export your CSVs and use the CRM Import tool to migrate your clients and deals. It takes just a few minutes. Our auto-mapping feature recognizes common column names, so most of your data will map automatically."
    },
    {
      q: "What if I'm mid-project?",
      a: "You can recreate or import your project, upload any notes or contracts, and continue working without missing a beat. Your clients won't notice any disruption."
    },
    {
      q: "What happens if I don't migrate before March 1?",
      a: "AND.CO will shut down, cutting off access to your data. Don't wait until the last week — export your data now while you still have access."
    },
    {
      q: "How long does migration take?",
      a: "Most users complete their migration in under 30 minutes. The CSV import handles the heavy lifting, and our setup wizard guides you through connecting your email and configuring your workspace."
    },
    {
      q: "Is there a free trial?",
      a: "Yes! Every new account gets a 7-day Pro trial. Combined with the ANDCO2026 promo code, you get 3 months for the price of 1 after your trial."
    },
    {
      q: "What features does ClientKit have that AND.CO didn't?",
      a: "ClientKit adds AI-powered tools like Client Firewall (risk detection), Scope Guard (scope creep prevention), cold outreach automation, CRM pipeline management, and profitability analytics — features designed for modern freelancers."
    },
  ];

  return (
    <>
      <Helmet>
        <title>Switch from AND.CO to ClientKit in 30 Minutes | Migration Guide</title>
        <meta name="description" content="AND.CO is shutting down March 1st. Migrate to ClientKit in 30 minutes – import your data, keep your clients, get 3 months for the price of 1." />
        <meta name="keywords" content="AND.CO alternative, AND.CO shutdown, freelance CRM, migrate from AND.CO, AND.CO replacement" />
        <meta property="og:title" content="Switch from AND.CO to ClientKit in 30 Minutes" />
        <meta property="og:description" content="AND.CO is shutting down March 1st. Migrate to ClientKit in 30 minutes and get 3 months for the price of 1." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourfreelanceros.com/andco-migration" />
        <link rel="canonical" href="https://yourfreelanceros.com/andco-migration" />
      </Helmet>

      <div className="min-h-screen bg-slate-950">
        {/* Urgency Banner */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-3 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-center gap-3 text-sm md:text-base">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">
              AND.CO shuts down in <strong>{timeLeft.days} days, {timeLeft.hours} hours</strong>
            </span>
            <span className="hidden md:inline">— Export your data now while you still can</span>
          </div>
        </div>

        {/* Header */}
        <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate("/")}
            >
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-white text-lg">ClientKit</span>
            </div>
            <Button 
              onClick={() => window.location.href = CTA_URL}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Start Migration
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 md:py-24 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-6">
              <Clock className="w-4 h-4 text-amber-400" />
              <span className="text-amber-300 text-sm font-medium">Migration Guide</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Switch from AND.CO to ClientKit in{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                30 Minutes
              </span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-4 max-w-2xl mx-auto">
              AND.CO is shutting down March 1st. You have just days to transition before losing access.
            </p>
            
            <p className="text-lg text-slate-300 mb-8">
              Here's how to switch to ClientKit — <strong>fast, simple, and chaos-free.</strong>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => window.location.href = CTA_URL}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-lg px-8 py-6"
              >
                Start Free Migration
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('comparison').scrollIntoView({ behavior: 'smooth' })}
                className="border-slate-700 text-slate-300 hover:text-white text-lg px-8 py-6"
              >
                See Why Users Switch
              </Button>
            </div>

            {/* Countdown Timer */}
            <div className="mt-12 flex justify-center gap-4">
              {[
                { value: timeLeft.days, label: "Days" },
                { value: timeLeft.hours, label: "Hours" },
                { value: timeLeft.minutes, label: "Minutes" },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-900 border border-slate-800 rounded-lg px-6 py-4 min-w-[100px]">
                  <div className="text-3xl font-bold text-white">{item.value}</div>
                  <div className="text-sm text-slate-500">{item.label}</div>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-4">Until AND.CO shuts down</p>
          </div>
        </section>

        {/* Comparison Section */}
        <section id="comparison" className="py-16 px-4 bg-slate-900/50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Why AND.CO Users Are Choosing ClientKit
              </h2>
              <p className="text-slate-400 text-lg">
                You're not just replacing a tool. You're upgrading your workflow.
              </p>
            </div>

            {/* Comparison Table */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="grid grid-cols-2 bg-slate-800">
                <div className="px-6 py-4 text-slate-400 font-medium">AND.CO Had This</div>
                <div className="px-6 py-4 text-indigo-400 font-medium">ClientKit Does This Better</div>
              </div>
              {comparisonData.map((row, idx) => (
                <div key={idx} className="grid grid-cols-2 border-t border-slate-800">
                  <div className="px-6 py-5 text-slate-400 flex items-center gap-3">
                    <row.icon className="w-5 h-5 text-slate-600" />
                    {row.andco}
                  </div>
                  <div className="px-6 py-5 text-white flex items-start gap-3 bg-indigo-500/5">
                    <CheckCircle2 className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                    {row.clientkit}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                What ClientKit Adds That AND.CO Didn't
              </h2>
              <p className="text-slate-400 text-lg">
                Modern tools for freelancers who want to work smarter, not harder.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((feature, idx) => (
                <Card key={idx} className="bg-slate-900 border-slate-800 hover:border-indigo-500/50 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 bg-indigo-500/10 rounded-lg flex items-center justify-center mb-4">
                      <feature.icon className="w-5 h-5 text-indigo-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-slate-400 text-sm">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Migration Steps */}
        <section className="py-16 px-4 bg-slate-900/50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Your Migration Checklist
              </h2>
              <p className="text-slate-400 text-lg">
                Complete your migration in under 30 minutes
              </p>
            </div>

            <div className="space-y-6">
              {migrationSteps.map((step, idx) => (
                <div key={idx} className="bg-slate-900 border border-slate-800 rounded-xl p-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 bg-indigo-500/10 text-indigo-400 text-sm font-medium px-4 py-2 rounded-bl-xl">
                    ~{step.time}
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-lg">{step.step}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <step.icon className="w-5 h-5 text-indigo-400" />
                        <h3 className="text-xl font-semibold text-white">{step.title}</h3>
                      </div>
                      <ul className="space-y-2">
                        {step.tasks.map((task, taskIdx) => (
                          <li key={taskIdx} className="flex items-center gap-2 text-slate-400">
                            <CheckCircle2 className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                            {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Total Time */}
            <div className="mt-8 text-center p-6 bg-indigo-500/10 border border-indigo-500/30 rounded-xl">
              <p className="text-indigo-300 text-lg">
                <strong>Total estimated time:</strong> ~30 minutes
              </p>
              <p className="text-slate-400 mt-2">
                Most users finish even faster with our auto-mapping CSV import
              </p>
            </div>
          </div>
        </section>

        {/* Special Offer Section */}
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border-indigo-500/30 overflow-hidden relative">
              <div className="absolute top-0 right-0 bg-amber-500 text-black text-sm font-bold px-4 py-2">
                LIMITED TIME
              </div>
              <CardContent className="p-8 md:p-12 text-center">
                <Gift className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Special Offer for AND.CO Users
                </h2>
                <p className="text-2xl text-indigo-300 mb-6">
                  Get <strong>3 months of ClientKit Pro</strong> for the price of 1
                </p>
                
                <ul className="text-left max-w-md mx-auto space-y-3 mb-8">
                  {[
                    "Full access to Client Firewall and Scope Guard",
                    "Cold outreach tools",
                    "Unlimited clients and deals",
                    "CSV import access",
                    "Gmail integration for email tracking"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-300">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="bg-slate-900/50 rounded-lg p-4 mb-6 inline-block">
                  <p className="text-slate-400 text-sm mb-1">Use code</p>
                  <p className="text-2xl font-mono font-bold text-white tracking-wider">{PROMO_CODE}</p>
                </div>

                <p className="text-amber-400 text-sm mb-6">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Valid through <strong>Feb 28, 2026</strong>
                </p>

                <Button 
                  size="lg"
                  onClick={() => window.location.href = CTA_URL}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-lg px-10 py-6"
                >
                  <Rocket className="w-5 h-5 mr-2" />
                  Redeem Offer
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 bg-slate-900/50">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-slate-400 text-lg">
                Common questions from AND.CO users making the switch
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                  >
                    <span className="text-white font-medium pr-4">{faq.q}</span>
                    {openFaq === idx ? (
                      <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === idx && (
                    <div className="px-6 pb-5 text-slate-400 border-t border-slate-800 pt-4">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Clock Is Ticking
            </h2>
            <p className="text-xl text-slate-400 mb-8 max-w-xl mx-auto">
              ClientKit is ready when AND.CO isn't. Migrate in minutes, not months — and stay focused on your clients, not your tools.
            </p>
            
            <Button 
              size="lg"
              onClick={() => window.location.href = CTA_URL}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-lg px-10 py-6"
            >
              Start Your Migration
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <p className="text-slate-500 text-sm mt-6">
              Free to start • No credit card required • Cancel anytime
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-slate-800 py-8 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-md flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-white">ClientKit</span>
            </div>
            <p className="text-slate-500 text-sm mb-4">
              The operating system for freelancers who've outgrown spreadsheets and chaos.
            </p>
            <p className="text-slate-600 text-xs">
              © {new Date().getFullYear()} ClientKit. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
